// Copyright (c) 2024 by Audere

import { cloverLabelingTypes } from "../data/schema/cloverLabelingSchemaV1";

import { MessageRole } from "../api/labelApiSlice";
import { celosiaLabelingTypes } from "../data/schema/celosiaLabelingSchemaV1";
import { mangroveLabelingTypes } from "../data/schema/mangroveLabelingSchemaV1";
import { proteaLabelingTypes } from "../data/schema/proteaLabelingSchemaV1";
import { flags } from "../data/schema/common";

// TODO: AUD-11190 - Add additional labeling types as needed
export type LabelKeys = (
  | typeof cloverLabelingTypes
  | typeof celosiaLabelingTypes
  | typeof mangroveLabelingTypes
  | typeof proteaLabelingTypes
)[number]["taskIDs"];

export interface LabelingSchema {
  labelingTypes: readonly LabelingType[];
  labelingTasks: LabelTask[];
  labelingQuestions: Question[];
  labelingSchemaVersion: string;
  personas?: PersonaOption[];
}

export interface PersonaOption {
  id: string;
  name: string;
  description: string;
}

export enum LabeledContent {
  conversation = "conversation",
  summary = "summary",
  message = "message",
  structuredData = "structuredData",
  translatedMessage = "translatedMessage",
}

export interface LabelingType {
  id: string;
  name: string;
  description: string;
  taskIDs: LabelKeys;
}

export interface LabelTask {
  id: string;
  name: string;
  questionIDs: string[];
  content: LabeledContent;
  instructions?: string;
}

export interface QuestionBase {
  id: string;
  name: string;
  type: "select" | "text" | "multiSelect";
  isRequired: boolean;
  addComment: boolean;
  commentLabel?: string;
  commentRequired?: string;
  displayFlags?: boolean;
  perFlag?: boolean;
  displayDefinition?: boolean;
  additionalInstructions?: string;
  roles?: MessageRole[];
  dependsOn?: {
    questionId: string;
    values: string[];
  };
  dependents?: { questionIds: string[]; values: string[] };
}

export interface Option {
  id: string;
  name: string;
}

export interface OptionWithDefinition extends Option {
  definition: string;
}

export interface FlagOption extends OptionWithDefinition {
  api: string;
  definition: string;
  styling: {
    svg: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    iconColor: string;
    borderColor: string;
    backgroundColor: string;
  };
}

export interface SelectQuestion extends QuestionBase {
  type: "select";
  options: Option[];
}

export interface TextQuestion extends QuestionBase {
  type: "text";
  addComment: false;
}

export interface MultiSelectQuestion extends QuestionBase {
  type: "multiSelect";
  options: Option[];
}

export type Question = SelectQuestion | TextQuestion | MultiSelectQuestion;

export const getLabelType = (
  typeID: string,
  types: readonly LabelingType[]
) => {
  return types.find((labelType) => labelType.id === typeID);
};

export const getLabelTask = (
  taskID: string,
  tasks: LabelTask[]
): LabelTask | undefined => {
  return tasks.find((task) => task.id === taskID);
};

export const getLabelTasks = (
  labelTypeID: string | undefined,
  tasks: LabelTask[],
  types: readonly LabelingType[]
): LabelTask[] => {
  const labelType = getLabelType(labelTypeID ?? "", types);
  if (labelType) {
    return labelType.taskIDs
      .map((taskID) => getLabelTask(taskID, tasks))
      .filter((task): task is LabelTask => !!task);
  } else {
    return [];
  }
};

export const getQuestion = (
  questionID: string,
  questions: Question[]
): Question | undefined => {
  return questions.find((question) => question.id === questionID);
};

export const getQuestions = (
  questionIDs: string[],
  questions: Question[],
  messageRole?: MessageRole
): Question[] => {
  const list = questionIDs
    .map((id) => getQuestion(id, questions))
    .filter((q): q is Question => !!q);

  if (!messageRole) return list;

  return list.filter((q) => (q.roles ?? []).includes(messageRole));
};

export function getFlagOption(flag: string): FlagOption | undefined {
  return flags.find((option: FlagOption) => option.api === flag);
}

export function getPersonaOption(
  personaId: string | undefined,
  personas: PersonaOption[] | undefined
): PersonaOption | undefined {
  if (!personaId) return undefined;
  return personas?.find((option) => option.id === personaId);
}
